import { DateTime } from 'luxon';
import { emptyArrayOf } from 'ui-components/default-props/empty';
import type { DateGroupedNotifications, GroupedNotification, Notification, NotificationHubState } from './notification-hub.types';
import { DateCategory, NotificationFilter, NotificationPriority } from './notification-hub.types';

const DATE_CATEGORY_LABELS: Record<DateCategory, string> = {
  [DateCategory.NEW]: 'New',
  [DateCategory.YESTERDAY]: 'Yesterday',
  [DateCategory.EARLIER_THIS_WEEK]: 'Earlier this week',
  [DateCategory.LAST_WEEK]: 'Last week',
  [DateCategory.EARLIER]: 'Earlier',
};

const DATE_CATEGORY_ORDER: DateCategory[] = [
  DateCategory.NEW,
  DateCategory.YESTERDAY,
  DateCategory.EARLIER_THIS_WEEK,
  DateCategory.LAST_WEEK,
  DateCategory.EARLIER,
];

const getDateCategory = (createdUtc: string): DateCategory => {
  const now = DateTime.now();
  const created = DateTime.fromISO(createdUtc);
  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.minus({ days: 1 });
  const startOfWeek = now.startOf('week');
  const startOfLastWeek = startOfWeek.minus({ weeks: 1 });

  if (created >= startOfToday) {
    return DateCategory.NEW;
  }
  if (created >= startOfYesterday) {
    return DateCategory.YESTERDAY;
  }
  if (created >= startOfWeek) {
    return DateCategory.EARLIER_THIS_WEEK;
  }
  if (created >= startOfLastWeek) {
    return DateCategory.LAST_WEEK;
  }
  return DateCategory.EARLIER;
};

const filterNotifications = (notifications: Notification[], activeFilter: NotificationFilter): Notification[] => {
  switch (activeFilter) {
    case NotificationFilter.ALL:
      return notifications.filter((n) => !n.isDismissed);
    case NotificationFilter.UNREAD:
      return notifications.filter((n) => !n.isRead && !n.isDismissed);
    case NotificationFilter.CRITICAL:
      return notifications.filter((n) => n.priority === NotificationPriority.CRITICAL && !n.isDismissed);
    case NotificationFilter.WARNING:
      return notifications.filter((n) => n.priority === NotificationPriority.WARNING && !n.isDismissed);
    case NotificationFilter.INFO:
      return notifications.filter((n) => n.priority === NotificationPriority.INFO && !n.isDismissed);
    default:
      return notifications.filter((n) => !n.isDismissed);
  }
};

const groupNotifications = (notifications: Notification[]): GroupedNotification[] => {
  if (notifications.length === 0) {
    return emptyArrayOf<GroupedNotification>();
  }

  const groupMap = new Map<string, GroupedNotification>();

  notifications.forEach((notification) => {
    const groupKey = `${notification.title}::${notification.description}`;
    if (groupMap.has(groupKey)) {
      const group = groupMap.get(groupKey);
      if (!group) return;
      group.groupedNotifications.push(notification);
      group.groupCount = group.groupedNotifications.length;
      if (notification.createdUtc > group.notification.createdUtc) {
        group.notification = notification;
      }
    } else {
      groupMap.set(groupKey, {
        notification,
        groupCount: 1,
        groupedNotifications: [notification],
        isGrouped: false,
      });
    }
  });

  const grouped = Array.from(groupMap.values()).map((group) => ({
    ...group,
    isGrouped: group.groupCount > 1,
  }));

  grouped.sort((a, b) => {
    const dateA = DateTime.fromISO(a.notification.createdUtc);
    const dateB = DateTime.fromISO(b.notification.createdUtc);
    return dateB.toMillis() - dateA.toMillis();
  });

  return grouped;
};

export const selectIsOpen = (state: NotificationHubState): boolean => state?.isOpen ?? false;

export const selectActiveFilter = (state: NotificationHubState): NotificationFilter => state?.activeFilter ?? NotificationFilter.ALL;

export const selectAllNotifications = (state: NotificationHubState): Notification[] => state?.notifications ?? emptyArrayOf<Notification>();

export const selectFilteredNotifications = (state: NotificationHubState): Notification[] => {
  const notifications = selectAllNotifications(state);
  const activeFilter = selectActiveFilter(state);
  return filterNotifications(notifications, activeFilter);
};

export const selectUnreadCount = (state: NotificationHubState): number => state?.unreadCount ?? 0;

export const selectCriticalCount = (state: NotificationHubState): number => state?.criticalCount ?? 0;

export const selectWarningCount = (state: NotificationHubState): number => state?.warningCount ?? 0;

export const selectInfoCount = (state: NotificationHubState): number => state?.infoCount ?? 0;

export const selectGroupedFilteredNotifications = (state: NotificationHubState): GroupedNotification[] => {
  const filtered = selectFilteredNotifications(state);
  return groupNotifications(filtered);
};

export const selectDateGroupedNotifications = (state: NotificationHubState): DateGroupedNotifications[] => {
  const grouped = selectGroupedFilteredNotifications(state);

  if (grouped.length === 0) {
    return emptyArrayOf<DateGroupedNotifications>();
  }

  const categoryMap = new Map<DateCategory, GroupedNotification[]>();

  grouped.forEach((groupedNotification) => {
    const category = getDateCategory(groupedNotification.notification.createdUtc);
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category)?.push(groupedNotification);
  });

  return DATE_CATEGORY_ORDER.filter((category) => categoryMap.has(category)).map((category) => {
    const notifications = categoryMap.get(category) ?? emptyArrayOf<GroupedNotification>();
    return {
      category,
      label: DATE_CATEGORY_LABELS[category],
      count: notifications.length,
      notifications,
    };
  });
};

export const selectIsLoading = (state: NotificationHubState): boolean => state?.isLoading ?? false;

export const selectHasError = (state: NotificationHubState): boolean => state?.hasError ?? false;
